import type {
  ContractContactAlertType,
  ContractContactBlockerCode,
  ContractContactGroup,
  ContractContactResource,
  ContractContactStatus,
} from "../types";

import { contactMethodRows, subscriptionKey } from "./occupant-payload";

/** Flattened contact info for a subscription. The person fields live on the
 * attached `contacts` resource, inlined under `relationships.contact.data`
 * with its attributes (the backend inlines related resources instead of a
 * top-level `included[]`). */
export interface ContractContactPerson {
  contactId: string | null;
  name: string;
  phone: string | null;
  email: string | null;
}

/**
 * Placeholder older versions of the Add contact dialog wrote before it asked
 * for a name — dropped here so it never reaches the list.
 */
const NAME_PLACEHOLDER = "NULL";

export const contractContactPerson = (contractContact: ContractContactResource): ContractContactPerson => {
  const contact = contractContact.relationships?.contact?.data;
  // `name` is whichever shape the contact holds — composing the halves here left
  // a contact migrated from a client (name in `fullname`) with no name at all.
  const name = (contact?.attributes?.name ?? "")
    .split(/\s+/)
    .filter((part) => part && part !== NAME_PLACEHOLDER)
    .join(" ")
    .trim();

  return {
    contactId: contact ? String(contact.id) : null,
    name,
    phone: contact?.attributes?.phone ?? null,
    email: contact?.attributes?.email ?? null,
  };
};

/**
 * Which opt-in action the row offers. `send` is an invitation that never went
 * out, `resend` one still open or refused. `null` means no button helps: the
 * contact is blocked or has no destination (the API refuses both), alerts are
 * off at the contract or company level (a setting, not a row), or the row
 * already receives.
 */
export type OptInAction = "send" | "resend" | null;

const OPT_IN_ACTIONS: Partial<Record<ContractContactBlockerCode, Exclude<OptInAction, null>>> = {
  opt_in_not_sent: "send",
  opt_in_pending: "resend",
  opt_in_refused: "resend",
};

/** What the contacts list needs from one subscription. */
export interface ContractContactRow {
  id: string;
  alertType: ContractContactAlertType;
  /** The contact's first + last name; empty when the contact is a bare method. */
  name: string;
  /** Where this row delivers — the backend's `alert_destination`, never a phone
   * column picked here: legacy data spreads SMS numbers across all three. */
  value: string;
  /** The user's own name for that destination — "Home", "Site office". `null`
   * when the channel carries no label, or on legacy rows with no channel. */
  label: string | null;
  status: ContractContactStatus;
  optInSentAt: string | null;
  respondedAt: string | null;
  serviceCall: boolean;
  /** Server-computed. Folds in the status, the alert type, the account's opt-in
   * setting and both kill switches — a `call` row receives without ever opting
   * in, so this can't be derived from `status`. */
  willReceiveAlerts: boolean;
  /** Display-ready reason it won't; `null` while it will. */
  blockerMessage: string | null;
  optInAction: OptInAction;
}

/**
 * The destination's label. Read off the resolved channel when the row has one;
 * legacy rows carry no `contact_channel_id`, so they are matched back to a
 * channel by the destination string instead.
 */
const contractContactLabel = (contractContact: ContractContactResource): string | null => {
  const channel = contractContact.relationships?.contact_channel;

  if (channel) {
    return channel.label || null;
  }

  const destination = contractContact.attributes.alert_destination;
  const channels = contractContact.relationships?.contact?.data?.attributes?.channels;

  if (!destination || !channels) {
    return null;
  }

  return channels.find((candidate) => candidate.value === destination)?.label || null;
};

export const contractContactRow = (contractContact: ContractContactResource): ContractContactRow => {
  const { name } = contractContactPerson(contractContact);
  // Both are served alongside their ids; rendering off the resolved string
  // keeps the list independent of the constant ids per environment.
  const alertType = contractContact.attributes.alert_type ?? "email";
  const status = contractContact.attributes.status ?? "not_sent";
  const blocker = contractContact.attributes.will_not_receive_reason ?? null;

  return {
    id: String(contractContact.id),
    alertType,
    name,
    value: contractContact.attributes.alert_destination ?? "",
    label: contractContactLabel(contractContact),
    status,
    optInSentAt: contractContact.attributes.opt_in_sent_at ?? null,
    respondedAt: contractContact.attributes.responded_at ?? null,
    serviceCall: Boolean(contractContact.attributes.is_service_call_enabled),
    willReceiveAlerts: Boolean(contractContact.attributes.will_receive_alerts),
    blockerMessage: blocker?.message ?? null,
    optInAction: blocker ? (OPT_IN_ACTIONS[blocker.code] ?? null) : null,
  };
};

/** Ties on `contact_id` come back in whatever order the backend happens to
 * return, so a row that was just written can jump — toggling a switch would
 * reshuffle the list under the cursor. Ordering by (contact, row id) here pins
 * every row where it was, whatever the refetch brings back. */
const byContactThenId = (contractContacts: ContractContactResource[]) =>
  [...contractContacts].sort((left, right) => {
    const leftContact = Number(left.relationships?.contact?.data?.id ?? 0);
    const rightContact = Number(right.relationships?.contact?.data?.id ?? 0);

    return leftContact === rightContact ? Number(left.id) - Number(right.id) : leftContact - rightContact;
  });

/**
 * Groups the flat list into one block per contact, ordered by contact then row
 * id. The endpoint paginates rows rather than contacts, so the list is fetched
 * whole (see `use-contract-contacts-service`) and grouped here.
 */
export const contractContactGroups = (contractContacts: ContractContactResource[]): ContractContactGroup[] => {
  const groups = new Map<string, ContractContactGroup>();

  for (const contractContact of byContactThenId(contractContacts)) {
    const { contactId, name } = contractContactPerson(contractContact);
    // A subscription always points at a contact; keying orphans by row id keeps
    // them visible (and deletable) instead of merging them into one block.
    const key = contactId ?? `row-${contractContact.id}`;
    const group = groups.get(key);

    if (group) {
      group.contractContacts.push(contractContact);
      continue;
    }

    groups.set(key, {
      contactId: contactId ?? "",
      name,
      isPrimaryContact: Boolean(contractContact.attributes.is_primary_contact),
      contractContacts: [contractContact],
    });
  }

  return [...groups.values()];
};

/** One alert box on a destination row: the type it subscribes to, whether it is
 * on, and the subscription behind it — the row an untick deletes. */
export interface ContactAlertToggle {
  alertType: ContractContactAlertType;
  checked: boolean;
  /** `null` when the box is unticked: there is no row to delete, ticking
   * creates one. */
  contractContactId: string | null;
}

/**
 * One destination of a contact, as the list renders it: a number or an address
 * with the alert boxes that apply to it. A phone can be called and texted; an
 * email can only be emailed.
 */
export interface ContactDestinationRow {
  key: string;
  kind: "phone" | "email";
  value: string;
  /** The user's own name for it — "Home", "Work". `null` when it has none. */
  label: string | null;
  alerts: ContactAlertToggle[];
}

/** Alert types offered per destination kind, in the order the design lists them. */
const ALERT_TYPES_BY_KIND: Record<ContactDestinationRow["kind"], ContractContactAlertType[]> = {
  phone: ["call", "sms"],
  email: ["email"],
};

/**
 * The contact's destinations with their alert boxes resolved — the rows the
 * table lists under one contact.
 *
 * Destinations come from the contact's own `channels`, not from the
 * subscriptions: a number that is on file with nothing ticked still has to be
 * listed, otherwise there is nowhere to turn an alert on. The ticks are then
 * matched back from the subscription rows.
 */
export const contactDestinationRows = (group: ContractContactGroup): ContactDestinationRow[] => {
  const attributes = group.contractContacts[0]?.relationships?.contact?.data?.attributes;
  // Same key the save path uses — alert type plus normalized destination: a
  // number can carry both a call and an SMS row, two numbers can both carry SMS,
  // and `514-300-5432` on file is the `5143005432` a row delivers to.
  const subscriptions = new Map(
    group.contractContacts.flatMap((contractContact) => {
      const { alert_type: alertType, alert_destination: destination } = contractContact.attributes;

      return alertType && destination ? [[subscriptionKey(alertType, destination), String(contractContact.id)] as const] : [];
    }),
  );

  const { phones, emails } = contactMethodRows(attributes, group.contractContacts);

  const toRow = (kind: ContactDestinationRow["kind"]) => (entry: { label: string; value: string }) => ({
    key: `${kind}-${entry.value}`,
    kind,
    value: entry.value,
    label: entry.label || null,
    alerts: ALERT_TYPES_BY_KIND[kind].map((alertType) => {
      const contractContactId = subscriptions.get(subscriptionKey(alertType, entry.value)) ?? null;

      return { alertType, checked: contractContactId !== null, contractContactId };
    }),
  });

  return [
    // `contactMethodRows` pads each list with a blank row so the drawer always
    // has somewhere to type; the table has nothing to show for one.
    ...phones.filter((phone) => phone.value !== "").map(toRow("phone")),
    ...emails.filter((email) => email.value !== "").map(toRow("email")),
  ];
};
